import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import {
  CanalRegistro,
  TipoUbicacion,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { EstadoEditorSitio, FiltrosSitios, RUTA_SITIOS } from "./sitios.rutas";

/**
 * Catalogo de sitios de trabajo.
 *
 * Es donde se decide lo que el negocio pidio poder decidir sitio por
 * sitio: si alli se checa con huella en un kiosco, desde el telefono, o
 * de las dos formas. En la mayoria de los sitios de cliente no habra
 * kiosco, y por eso el canal se configura y no se infiere.
 *
 * El tipo separa a la gente propia de la asignada a clientes. Cuando es
 * SITIO_CLIENTE, el cliente que se elige es el que despues recibe su
 * lista de asistencia.
 *
 * Alta y edicion son una pagina aparte (editor-ubicacion), no un dialogo:
 * con el mapa dentro el dialogo no cabia en la ventana y se cortaba.
 */
@Component({
  selector: "app-ubicaciones",
  standalone: true,
  templateUrl: "./ubicaciones.component.html",
  styleUrls: ["./ubicaciones.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class UbicacionesComponent implements OnInit {
  ubicaciones: Ubicacion[] = [];
  loading = false;

  filtroNombre = "";
  filtroTipo: TipoUbicacion = null;
  /** Por omision el catalogo muestra solo lo vigente. */
  filtroEstatus = "ACTIVO";

  constructor(
    private asistenciaService: AsistenciaService,
    private router: Router,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.restaurarFiltros();
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    const filtro = new Ubicacion();
    filtro.estatus = this.filtroEstatus;

    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarUbicaciones(filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.ubicaciones = this.aplicarFiltrosLocales(respuesta?.list ?? []);
        },
        error: () => {
          this.ubicaciones = [];
          this.msg.errorCall("No se pudieron consultar los sitios.", null, null);
        },
      });
  }

  limpiarFiltros(): void {
    this.filtroNombre = "";
    this.filtroTipo = null;
    this.filtroEstatus = "ACTIVO";
    this.consultar();
  }

  nuevoSitio(): void {
    this.router.navigate([RUTA_SITIOS, "alta"], {
      state: { filtros: this.filtrosActuales() } as EstadoEditorSitio,
    });
  }

  /**
   * El sitio viaja en history.state para que el editor abra sin volver a
   * consultar; si la pagina se recarga, el editor lo busca por el id de
   * la URL.
   */
  editarSitio(ubicacion: Ubicacion): void {
    this.router.navigate([RUTA_SITIOS, "edicion", ubicacion.id], {
      state: { ubicacion: { ...ubicacion }, filtros: this.filtrosActuales() } as EstadoEditorSitio,
    });
  }

  desactivar(ubicacion: Ubicacion): void {
    this.msg.questionCall(
      `¿Dar de baja el sitio ${ubicacion.nombre}? Las checadas históricas se conservan.`,
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.desactivarUbicacion(ubicacion).subscribe({
          next: (res) => this.trasCambiarEstatus(res, "El sitio quedó dado de baja."),
          error: () => this.msg.errorCall("No se pudo dar de baja el sitio.", null, null),
        });
      }
    );
  }

  reactivar(ubicacion: Ubicacion): void {
    this.asistenciaService.reactivarUbicacion(ubicacion).subscribe({
      next: (res) => this.trasCambiarEstatus(res, "El sitio quedó activo."),
      error: () => this.msg.errorCall("No se pudo reactivar el sitio.", null, null),
    });
  }

  etiquetaTipo(tipo: TipoUbicacion): string {
    const etiquetas: Record<string, string> = {
      OFICINA_PROPIA: "Oficina propia",
      SITIO_CLIENTE: "Sitio de cliente",
      HOME_OFFICE: "Home office",
    };
    return etiquetas[tipo] ?? tipo ?? "-";
  }

  etiquetaCanal(canal: CanalRegistro): string {
    const etiquetas: Record<string, string> = {
      KIOSCO: "Solo kiosco",
      APP: "Solo teléfono",
      AMBOS: "Kiosco y teléfono",
    };
    return etiquetas[canal] ?? canal ?? "-";
  }

  etiquetaGeocerca(ubicacion: Ubicacion): string {
    if (!ubicacion.exigeGeocerca) {
      return "Sin geocerca";
    }
    return `${ubicacion.radioMetros} m`;
  }

  claseEstatus(estatus: string): string {
    // Modificadores globales de table-page__status-badge (styles.css).
    return estatus === "ACTIVO"
      ? "table-page__status-badge is-active"
      : "table-page__status-badge is-inactive";
  }

  esActivo(ubicacion: Ubicacion): boolean {
    return ubicacion.estatus === "ACTIVO";
  }

  // ------------------------------------------------------------------

  /**
   * El nombre y el tipo se filtran en el navegador porque el catalogo de
   * sitios de una empresa es corto -decenas, no miles- y una ida al
   * servidor por cada tecla no compra nada.
   */
  private aplicarFiltrosLocales(lista: Ubicacion[]): Ubicacion[] {
    const texto = this.filtroNombre?.trim().toLowerCase();
    return lista.filter((sitio) => {
      if (this.filtroTipo && sitio.tipo !== this.filtroTipo) {
        return false;
      }
      if (!texto) {
        return true;
      }
      const nombre = `${sitio.nombre ?? ""} ${sitio.nombreCliente ?? ""}`.toLowerCase();
      return nombre.includes(texto);
    });
  }

  /** Los filtros se van con el editor y vuelven con el: nadie quiere volver a teclearlos. */
  private filtrosActuales(): FiltrosSitios {
    return {
      nombre: this.filtroNombre,
      tipo: this.filtroTipo,
      estatus: this.filtroEstatus,
    };
  }

  private restaurarFiltros(): void {
    const filtros = (history.state as EstadoEditorSitio | undefined)?.filtros;
    if (!filtros) {
      return;
    }
    this.filtroNombre = filtros.nombre ?? "";
    this.filtroTipo = filtros.tipo ?? null;
    this.filtroEstatus = filtros.estatus || "ACTIVO";
  }

  private trasCambiarEstatus(respuesta: any, exito: string): void {
    if (respuesta?.pCodRetorno && respuesta.pCodRetorno !== "00000") {
      this.msg.warningCall(respuesta.pMensaje, null, null);
      return;
    }
    this.msg.successCall(exito, null, null);
    this.consultar();
  }
}
